import React, { ReactNode, useCallback, useReducer } from "react";

import { faFilters } from "@fortawesome/pro-duotone-svg-icons";
import { Grid, Popper, PopperProps } from "@material-ui/core";
import styled from "styled-components/macro";

import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { Chip } from "components/Chip";
import {
  BusinessField,
  BusinessFieldExtraOption,
} from "components/Form/Fields/BusinessField/BusinessField";
import { Column } from "components/Layout";

import {
  BUSINESS_ROLE_FILTERS,
  BUYER,
  DEPLOYMENT,
  IN_SALE,
  LIVESTOCK_AGENT,
  TRANSPORTER,
  VENDOR,
} from "constants/businesses";

import { useToggle } from "hooks";
import { AutocompleteRenderOptionState } from "@material-ui/lab";
import { Key } from "constants/hotKey";
import { InSaleRenderOption } from "./BusinessFieldRenderOption";

interface PopDownOnlyProps extends PopperProps {
  style: object;
}

// Never flips up, so cap the listbox to the space below (--available-height).
const MIN_LIST_HEIGHT = 120;
const SCREEN_EDGE_GAP = 16;

const getSpaceBelow = (anchorEl: PopperProps["anchorEl"]): number | null => {
  const anchor = typeof anchorEl === "function" ? anchorEl() : anchorEl;
  if (!anchor) {
    return null;
  }
  const { bottom } = anchor.getBoundingClientRect();
  return Math.max(
    window.innerHeight - bottom - SCREEN_EDGE_GAP,
    MIN_LIST_HEIGHT,
  );
};

const PopDownOnly: React.FC<PopDownOnlyProps> = ({ style, ...props }) => {
  const spaceBelow = getSpaceBelow(props.anchorEl);
  return (
    // Prevents the dropdown becoming a dropup ever.  Stops the possibility of flipping from dropup to dropdown.
    // https://github.com/mui/material-ui/issues/21661
    <Popper
      {...props}
      style={{
        ...style,
        height: 0,
        ...(spaceBelow !== null && {
          "--available-height": `${spaceBelow}px`,
        }),
      }} // width is passed in 'style' prop
    />
  );
};

interface FilterChipProps {
  state: State;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Filter action dispatch
  dispatch: React.Dispatch<any>;
  filterKey: string;
  filterLabel: string;
}

const FilterChip: React.FC<FilterChipProps> = ({
  state,
  dispatch,
  filterKey,
  filterLabel,
}) => (
  <div
    className="cursor-pointer"
    role="button"
    tabIndex={0}
    onClick={e => {
      dispatch(filterKey);
      e.stopPropagation();
    }}
    onKeyUp={e => {
      if (e.key === Key.ENTER) {
        dispatch(filterKey);
        e.stopPropagation();
      }
    }}
  >
    <Chip
      color={state[filterKey] ? "white" : "black"}
      backgroundColor={state[filterKey] ? "warning" : null}
    >
      {filterLabel}
    </Chip>
  </div>
);

interface ListboxComponentProps {
  children?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Filter state object
  businessTypeFilterState: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Filter action dispatch
  businessTypeFilterDispatch: React.Dispatch<any>;
  showAllFilters: boolean;
  toggleShowAllFilters: () => void;
  hideFilters: boolean;
}

type Ref = HTMLUListElement;

// The Autocomplete dims disabled options - undo that for section headings so
// they stay readable, while still being unselectable.
const Listbox = styled.ul`
  && {
    max-height: min(40vh, var(--available-height, 40vh));
  }

  li[aria-disabled="true"]:has([data-section-heading]),
  li[aria-disabled="true"]:has([data-subsection-heading]) {
    opacity: 1;
  }

  li:has([data-subsection-heading]) {
    min-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const ListboxComponent = React.forwardRef<Ref, ListboxComponentProps>(
  (props, ref) => {
    // Normal Search Results list, with the filter added at the top.
    const {
      children,
      businessTypeFilterDispatch,
      businessTypeFilterState,
      showAllFilters,
      toggleShowAllFilters,
      hideFilters,
      ...other
    } = props;

    const toggleShowAllFiltersOnKeyUp = (
      e: React.KeyboardEvent<HTMLDivElement>,
    ) => {
      if (e.key === Key.ENTER) {
        toggleShowAllFilters();
      }
    };

    return (
      <Listbox {...other} ref={ref}>
        {!hideFilters && (
          <Column fullHeight full padding={2}>
            <Grid container>
              <FilterChip
                state={businessTypeFilterState}
                dispatch={businessTypeFilterDispatch}
                filterKey={IN_SALE}
                filterLabel="In Sale"
              />
              <FilterChip
                state={businessTypeFilterState}
                dispatch={businessTypeFilterDispatch}
                filterKey={BUYER}
                filterLabel="Buyer"
              />
              <FilterChip
                state={businessTypeFilterState}
                dispatch={businessTypeFilterDispatch}
                filterKey={VENDOR}
                filterLabel="Vendor"
              />
              {(showAllFilters || businessTypeFilterState.TRANSPORTER) && (
                <FilterChip
                  state={businessTypeFilterState}
                  dispatch={businessTypeFilterDispatch}
                  filterKey={TRANSPORTER}
                  filterLabel="Transporter"
                />
              )}
              {(showAllFilters || businessTypeFilterState.DEPLOYMENT) && (
                <FilterChip
                  state={businessTypeFilterState}
                  dispatch={businessTypeFilterDispatch}
                  filterKey={DEPLOYMENT}
                  filterLabel="Agencies"
                />
              )}
              {(showAllFilters || businessTypeFilterState.LIVESTOCK_AGENT) && (
                <FilterChip
                  state={businessTypeFilterState}
                  dispatch={businessTypeFilterDispatch}
                  filterKey={LIVESTOCK_AGENT}
                  filterLabel="Livestock Agents"
                />
              )}

              <div
                tabIndex={0}
                role="button"
                onClick={toggleShowAllFilters}
                onKeyUp={toggleShowAllFiltersOnKeyUp}
                className="cursor-pointer"
              >
                <Chip backgroundColor="primary" color="white">
                  {showAllFilters ? "Less" : "More"}
                  <FaIcon icon={faFilters} />
                </Chip>
              </div>
            </Grid>
          </Column>
        )}
        {children}
      </Listbox>
    );
  },
);

interface BusinessFieldWithFilterProps {
  name: string;
  label: string;
  disabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Default filter configuration
  defaultBusinessFilter?: any[];
  allowAddNew?: boolean;
  renderOption?: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Business option object
    option: any,
    state: AutocompleteRenderOptionState,
  ) => React.ReactNode;
  renderOptionHeadings?: boolean;
  globalBusinesses?: boolean;
  excludeIds?: string[];
  prioritisedSections?: { heading: string; ids: string[] }[];
  extraOptions?: BusinessFieldExtraOption[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Business ID or null
  onChangeExtra?: (newValue: any) => void;
  hideListboxComponent?: boolean;
  hideFilteredCount?: boolean;
  hideFilters?: boolean;
  searchFields?: string[];
  placeholder?: string;
  openOnFocus?: boolean;
  searchToListAll?: boolean;
  trailingSections?: { heading: string; ids: string[] }[];
  onInputChange?: (event: unknown, value: string) => void;
}

export const BusinessFieldWithFilter: React.FC<
  BusinessFieldWithFilterProps
> = ({
  defaultBusinessFilter = [],
  renderOption,
  hideListboxComponent = false,
  hideFilteredCount = false,
  hideFilters = false,
  ...props
}) => {
  const businessTypeFilterReducer = (state, action) => {
    if (state[action] === true) {
      return {
        ...state,
        [action]: false,
      };
    }
    return {
      ...state,
      [action]: true,
    };
  };

  const [businessTypeFilterState, businessTypeFilterDispatch] = useReducer(
    businessTypeFilterReducer,
    defaultBusinessFilter.reduce((acc, cur) => {
      acc[cur] = true;
      return acc;
    }, {}),
  );

  const [showAllFilters, toggleShowAllFilters] = useToggle(false);

  const listboxProps = {
    businessTypeFilterDispatch,
    businessTypeFilterState,
    showAllFilters,
    toggleShowAllFilters,
    hideFilters,
  };

  // Apply any extra active business type filters.
  const keysToFilter = Object.entries(businessTypeFilterState).reduce(
    (acc, [key, value]) => {
      if (value === true) {
        acc.push(key);
      }
      return acc;
    },
    [],
  );

  // Adjust the filtering function to push a specific short code result to the top.
  // This is defined by the type of sale, and who is searching, so memoize the function call
  const extraFilter = useCallback(
    option => {
      // If extra filters are selected, apply them too.

      if (keysToFilter.length > 0) {
        if (!keysToFilter.every(k => option[BUSINESS_ROLE_FILTERS[k]])) {
          return false;
        }
      }

      return true;
    },
    [keysToFilter],
  );

  return (
    <BusinessField
      label={undefined}
      name={undefined}
      {...props}
      renderOption={renderOption || InSaleRenderOption}
      ListboxComponent={hideListboxComponent ? undefined : ListboxComponent}
      ListboxProps={hideListboxComponent ? undefined : listboxProps}
      extraFilter={extraFilter}
      PopperComponent={PopDownOnly}
      activeOnly
      hideFilteredCount={hideFilteredCount}
    />
  );
};
